import type { DirectionalAction } from '@whaletail/signal-engine';
import type { DiceFace } from '../../lib/diceRoll';
import { STRATEGY_NAMES, type StrategyKey } from '../../lib/modelNames';
import { Btc15DiceRoll } from './Btc15DiceRoll';
import { StrategyHeading } from './StrategyHeading';
import styles from './Btc15ModelComparison.module.css';

export interface ModelPrediction {
  action: DirectionalAction;
  probability: number;
}

export interface FittedModelMeta {
  sampleCount: number;
  /** Held-out (walk-forward) accuracy from the fit's own validation, 0-1. Null if not enough data existed yet to compute one. */
  walkForwardAccuracy: number | null;
}

const ACTION_ARROW: Record<DirectionalAction, string> = { UP: '↗', DOWN: '↘' };
const ACTION_CLASS: Record<DirectionalAction, string> = {
  UP: styles.actionUp,
  DOWN: styles.actionDown,
};

function formatMeta(meta: FittedModelMeta): string {
  const accuracyText =
    meta.walkForwardAccuracy === null
      ? 'held-out accuracy not yet available'
      : `${(meta.walkForwardAccuracy * 100).toFixed(0)}% held-out accuracy`;
  return `Fit on ${meta.sampleCount} live rounds · ${accuracyText}.`;
}

function ModelColumn({
  strategy,
  badge,
  prediction,
  meta,
  placeholder,
  note,
}: {
  strategy: StrategyKey;
  badge: string;
  prediction: ModelPrediction | null;
  meta?: FittedModelMeta | null;
  placeholder?: string;
  /** A caveat on this round's call, shown under it. */
  note?: string;
}) {
  return (
    <div className={`${styles.column} ${prediction ? '' : styles.columnMuted}`}>
      <div className={styles.columnHeader}>
        <StrategyHeading strategy={strategy} />
        <span className={styles.badge}>{badge}</span>
      </div>
      {prediction ? (
        <>
          <div className={styles.predictionRow}>
            <span className={`${styles.action} ${ACTION_CLASS[prediction.action]}`}>
              {ACTION_ARROW[prediction.action]} {prediction.action}
            </span>
            <span className={styles.probability}>
              {(prediction.probability * 100).toFixed(1)}%
            </span>
          </div>
          {note && <p className={styles.placeholder}>{note}</p>}
          {meta && <p className={styles.placeholder}>{formatMeta(meta)}</p>}
        </>
      ) : (
        <p className={styles.placeholder}>{placeholder}</p>
      )}
    </div>
  );
}

const NOT_YET_TRAINED_PLACEHOLDER =
  'Waiting on enough live rounds to fit a first model. Refit runs daily against the growing candle archive — see packages/signal-engine for details. Not wired into any live recommendation.';

/**
 * Shows V1's live heuristic call next to V2's, V3's, and V4's fitted-model
 * calls, side by side (stacking on narrow screens). `v2Prediction`/
 * `v3Prediction`/`v4Prediction` are null until their own daily scheduled
 * refit (btc15-model-refit-scheduled.ts / btc15-v4-refit-scheduled.ts) has
 * fit a first model against the live candle archive — that's the normal
 * state early on, not an error. V4 additionally needs both V2 and V3 fitted
 * models to compute its blend inputs, so it stays untrained even longer
 * than either on its own. Once a model exists, its sample count and
 * held-out accuracy are shown alongside it (`v2Meta`/`v3Meta`/`v4Meta`)
 * rather than a bare probability, since a fit on a still-small live sample
 * needs that context to be read honestly.
 */
export function Btc15ModelComparison({
  v1Prediction,
  v1Skipped = false,
  v2Prediction,
  v2Meta,
  v3Prediction,
  v3Meta,
  v4Prediction,
  v4Meta,
  coinFlipPrediction,
  dicePrediction,
  diceFace,
}: {
  v1Prediction: ModelPrediction;
  /** The live model made no call this round; `v1Prediction` is only the side its score leans to. */
  v1Skipped?: boolean;
  v2Prediction: ModelPrediction | null;
  v2Meta?: FittedModelMeta | null;
  v3Prediction: ModelPrediction | null;
  v3Meta?: FittedModelMeta | null;
  v4Prediction?: ModelPrediction | null;
  v4Meta?: FittedModelMeta | null;
  /** Always non-null once a round exists — no fitted model to wait on, unlike V2/V3/V4 — see predictCoinFlip(). Optional only so existing call sites without it keep compiling. */
  coinFlipPrediction?: ModelPrediction | null;
  /** Same as coinFlipPrediction — always non-null once a round exists — see predictDice(). Gates whether the dice column renders at all; the actual rolled face comes separately via `diceFace` since Btc15DiceRoll animates the literal face, not just its derived action/probability. Optional only so existing call sites without it keep compiling. */
  dicePrediction?: ModelPrediction | null;
  /** The literal 1-6 face this round's dice prediction rolled — see predictDice(). Drives the animated die in the dice column below; null/undefined shows it in its "waiting for a round" state. */
  diceFace?: DiceFace | null;
}) {
  return (
    <section className={styles.card} aria-labelledby="btc15-comparison-heading">
      <h2 id="btc15-comparison-heading" className={styles.heading}>
        Model comparison
      </h2>
      <div className={styles.grid}>
        <ModelColumn
          strategy="v1"
          badge="Current model"
          prediction={v1Prediction}
          note={
            v1Skipped
              ? 'Skipped this round. This is only the side its score leans to, not a call.'
              : undefined
          }
        />
        <ModelColumn
          strategy="v2"
          badge={v2Prediction ? 'Auto-fit daily' : 'Not yet trained'}
          prediction={v2Prediction}
          meta={v2Meta}
          placeholder={NOT_YET_TRAINED_PLACEHOLDER}
        />
        <ModelColumn
          strategy="v3"
          badge={v3Prediction ? 'Auto-fit daily' : 'Not yet trained'}
          prediction={v3Prediction}
          meta={v3Meta}
          placeholder={NOT_YET_TRAINED_PLACEHOLDER}
        />
        <ModelColumn
          strategy="v4"
          badge={v4Prediction ? 'Auto-fit daily' : 'Not yet trained'}
          prediction={v4Prediction ?? null}
          meta={v4Meta}
          placeholder={NOT_YET_TRAINED_PLACEHOLDER}
        />
      </div>

      {(coinFlipPrediction || dicePrediction) && (
        <div className={styles.baselineSection}>
          <p className={styles.baselineNote}>
            Reference baselines — tracked the same way as every model above, never wired
            into any live recommendation.
          </p>
          <div className={styles.baselineRow}>
            {coinFlipPrediction && (
              <span className={styles.baselineStat}>
                <span className={styles.baselineStatLabel}>
                  {STRATEGY_NAMES.coinflip.descriptor}
                  <span className={styles.nickname}>{STRATEGY_NAMES.coinflip.name}</span>
                </span>
                <span
                  className={`${styles.baselineStatAction} ${ACTION_CLASS[coinFlipPrediction.action]}`}
                >
                  {ACTION_ARROW[coinFlipPrediction.action]} {coinFlipPrediction.action}
                </span>
                <span className={styles.baselineStatValue}>
                  {(coinFlipPrediction.probability * 100).toFixed(1)}%
                </span>
              </span>
            )}
            {dicePrediction && <Btc15DiceRoll face={diceFace ?? null} />}
          </div>
        </div>
      )}
    </section>
  );
}
